import { List, Datagrid, FunctionField, RaRecord, useListContext, useRefresh } from 'react-admin';
import { MaskingCell, ViaIdCell, ResourcesCell, NameCell, CountryCell } from './cells';
import { CostParameters } from './CostParameters';
import { TradeAllowed } from './TradeAllowed';
import { CompanyModules } from './CompanyModules';
import { menuLabel } from '../menuConfig';
import { ExchangeAccount } from './ExchangeAccount';
import { useFeature } from '../featureFlags/FeatureGate';

/**
 * Spółki obsługiwane przez instancję — lista, wyłącznie odczyt.
 *
 * ## Po co ten ekran
 *
 * Zasób `companies` był w menu (pozycja „Spółki" w sekcji administracyjnej)
 * i w API, ale **nie był zarejestrowany w `<Admin>`** — wejście kończyło się
 * ekranem „Not Found". Wyszło przy przejściu po całym froncie 22.09.2026.
 *
 * Instancja obsługuje dwie spółki klienta i cała aplikacja ma je rozpoznawać
 * PO DANYCH, nie po numerach wpisanych w kod (CLAUDE.md: bez hardkodów
 * `companyId`). Ekran pokazuje, co system o nich wie — razem z trybem
 * maskowania lokalizacji, bo to on rozstrzyga, jak wygląda adres wystawiony
 * na giełdę.
 *
 * Trybu maskowania tu nie edytujemy (ustawia się go w kolejce publikacji).
 * Edytujemy za to parametry kosztu dolotu (I.02) — stawki od Marathonu, bez
 * których koszt przy kandydatach jest nieznany. Zapis wymaga uprawnienia
 * sekcji administracyjnej i zostawia wpis w dzienniku działań.
 *
 * Tak samo prawo spółki do Trade i giełd (ustalenie ze statusu 29.09.2026:
 * konta giełdowe ma tylko spółka polska) — blokadę spółki bez kont zdejmuje
 * się tutaj, z powodem, gdy spółka dostanie konto.
 *
 * Moduły spółki (T.01, 06.10.2026): dobór modułów dla firmy — przełącznik
 * z powodem, wyłączony moduł znika z menu i kart, a API odmawia jego tras.
 *
 * Konto giełdowe wspólne (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 18b) —
 * kolumna „Konto giełdowe” tylko z przełącznikiem `exchangeBothCompanies`.
 */
/** Kolumna „Konto giełdowe” — potrzebuje wszystkich spółek z listy (wybór i nazwa). */
const ExchangeAccountColumn = ({ record, onSaved }: { record: RaRecord; onSaved: () => void }) => {
  const { data } = useListContext();
  return <ExchangeAccount record={record} companies={data ?? []} onSaved={onSaved} />;
};

export const CompanyList = () => {
  const refresh = useRefresh();
  const sharedAccountOn = useFeature('exchangeBothCompanies');
  return (
  <List
    pagination={false}
    perPage={25}
    title={menuLabel('companies')}
    sort={{ field: 'name', order: 'ASC' }}
  >
    <Datagrid rowClick={false} bulkActionButtons={false}>
      <FunctionField label="Spółka" render={(r: RaRecord) => <NameCell record={r} />} />
      <FunctionField label="Kraj" render={(r: RaRecord) => <CountryCell record={r} />} />
      <FunctionField
        label="Id w VIA"
        render={(r: RaRecord) => <ViaIdCell record={r} />}
      />
      <FunctionField
        label="Maskowanie lokalizacji"
        render={(r: RaRecord) => <MaskingCell record={r} />}
      />
      <FunctionField
        label="Trade i giełdy"
        render={(r: RaRecord) => <TradeAllowed record={r} onSaved={refresh} />}
      />
      {sharedAccountOn && (
        <FunctionField
          label="Konto giełdowe"
          render={(r: RaRecord) => <ExchangeAccountColumn record={r} onSaved={refresh} />}
        />
      )}
      <FunctionField
        label="Moduły"
        render={(r: RaRecord) => <CompanyModules record={r} onSaved={refresh} />}
      />
      <FunctionField
        label="Koszt dolotu"
        render={(r: RaRecord) => <CostParameters record={r} onSaved={refresh} />}
      />
      <FunctionField label="W systemie" render={(r: RaRecord) => <ResourcesCell record={r} />} />
    </Datagrid>
  </List>
  );
};
